import React, { useContext } from "react";
import { AppContext } from "../context/app.context";

/**
 * Time Machine #114 Phase 4 — drop-in wrapper that puts a modal's body into
 * read-only mode while the app is time-traveling.
 *
 * Why one wrapper instead of touching every input/button: HTML's native
 * `<fieldset disabled>` recursively disables every descendant form control
 * (input, select, textarea, button) without any per-control prop wiring.
 * This gives us structural certainty (a future button added to a modal
 * inherits read-only behavior automatically) AND minimizes per-modal edits
 * to two lines: import + wrap.
 *
 * Usage inside a modal:
 *
 *   <ReadOnlyShield>
 *     ...all the existing modal body, inputs, buttons, etc...
 *   </ReadOnlyShield>
 *
 * When isTimeTraveling is false, this is a transparent passthrough — no
 * fieldset, no banner, no DOM nesting changes.
 *
 * When isTimeTraveling is true:
 *   - A small banner appears at the top of the modal body
 *   - The whole body is inside `<fieldset disabled>` so nothing is clickable
 *   - The cursor on hover-blocked elements becomes "not-allowed"
 *
 * The mutation-layer guards in ModelTsContext + the processDragNDrop guard
 * in ScheduleContext are the structural defenses; this component is the
 * UI affordance that makes the read-only state legible to the user.
 */
const ReadOnlyShield = ({ children }) => {
  const { isTimeTraveling, timeTravelTimestamp } = useContext(AppContext);

  if (!isTimeTraveling) {
    return <>{children}</>;
  }

  const isoUtc =
    timeTravelTimestamp instanceof Date ? timeTravelTimestamp.toISOString() : "";
  const localPretty =
    timeTravelTimestamp instanceof Date
      ? timeTravelTimestamp.toLocaleString()
      : "";

  return (
    <>
      <div
        role="status"
        data-testid="read-only-shield-banner"
        style={{
          backgroundColor: "#704214",
          color: "#f5deb3",
          borderBottom: "2px solid #d4a373",
        }}
        className="px-4 py-2 text-sm font-bold"
        title={isoUtc ? `UTC: ${isoUtc}` : undefined}
      >
        <span aria-hidden="true">{"\u{1F552}"}</span>
        &nbsp;Read-only — viewing as of {localPretty}
      </div>
      <fieldset
        disabled
        data-testid="read-only-shield-fieldset"
        // Reset default fieldset styling so it doesn't disturb modal layout
        style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
      >
        {children}
      </fieldset>
    </>
  );
};

export default ReadOnlyShield;
